import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { getProjectManager, useProjectDataContext, GET_PREDECESSOR_SOURCE } from 'domain/shared/newProject';
import { reuseGroups, ReuseGroup, PredecessorSource } from './types';
import { buildSearchString, reuseGroupConfig, selectReuseSource } from './utils';
import useReuseDataService, { TransferFormValues } from './useReuseDataService';

interface UseReuseDataResult {
  predecessor: PredecessorSource | null;
  groupData: Record<ReuseGroup, unknown[]>;
  groupsToTransfer: ReuseGroup[];
  groupsToShow: ReuseGroup[];
  loading: boolean;
  isSubmitting: boolean;
  transferData: (values: TransferFormValues, groups: ReuseGroup[]) => Promise<ReuseGroup[]>;
}

export const useReuseData = (): UseReuseDataResult => {
  const { data: project } = useProjectDataContext();
  const { id: projectId, name: codeName, accountData } = project;
  const accountId = accountData?.accountId;

  const { data, loading } = useQuery<{ baseProjects?: PredecessorSource[] }>(GET_PREDECESSOR_SOURCE, {
    variables: { searchString: codeName && accountId ? buildSearchString(codeName, accountId) : '' },
    fetchPolicy: 'cache-and-network',
    skip: !projectId || !codeName || !accountId,
  });

  const { transferData, isSubmitting } = useReuseDataService(projectId, getProjectManager(project));

  const groups = useMemo(() => {
    const { predecessor, transferableGroups, isFirstReuse } = selectReuseSource(data?.baseProjects, projectId);

    const groupData = reuseGroups.reduce(
      (acc, group) => {
        acc[group] = reuseGroupConfig[group].extract(predecessor ?? undefined);
        return acc;
      },
      {} as Record<ReuseGroup, unknown[]>,
    );

    const groupsToTransfer = transferableGroups.filter(group => groupData[group].length > 0);
    const groupsToShow = isFirstReuse ? [...reuseGroups] : groupsToTransfer;

    return { predecessor, groupData, groupsToTransfer, groupsToShow };
  }, [data, projectId]);

  return {
    ...groups,
    loading,
    isSubmitting,
    transferData,
  };
};
